package com.devsusana.portfolio.pages.posts.es

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.blog.*
import com.varabyte.kobweb.core.Page

@Page("/posts/es/on-device-ai-local-rag-android")
@Composable
fun OnDeviceAiLocalRagAndroidPageEs() {
    ArticleScaffold(
        title = "IA On-Device y RAG Local en Android: Ejecutando Gemma con MediaPipe",
        date = "2026-09-28",
        readingTime = "10 min de lectura",
        tags = listOf("Android", "On-Device AI", "Gemma", "MediaPipe", "Local RAG", "Kotlin"),
        isSpanish = true
    ) {
        ArticleSectionHeader("1. El Cambio de Paradigma: IA en el Dispositivo vs. APIs en la Nube")

        ArticleParagraph(
            "Cuando nos planteamos añadir inteligencia conversacional a una aplicación Android, el reflejo habitual suele ser consumir una API REST en la nube (OpenAI, Gemini Cloud, Claude). Sin embargo, al crear software para profesionales autónomos en el mundo real —como profesores particulares a domicilio que gestionan horarios de menores, notas académicas e información de pagos—, esa vía introduce compromisos críticos inasumibles:"
        )

        ComparisonTable(
            headers = listOf("Dimensión", "IA en la Nube (APIs REST)", "IA On-Device (MediaPipe + Gemma en HomeTutorPro)"),
            rows = listOf(
                listOf(
                    "Coste Operativo",
                    "Facturación recurrente por tokens que escala linealmente con el uso. Vulnerabilidad en márgenes.",
                    "0,00 € por petición. El cómputo se realiza en el silicio del dispositivo. Escalabilidad infinita a coste marginal cero."
                ),
                listOf(
                    "Privacidad y RGPD",
                    "Datos personales de alumnos (nombres, teléfonos, precios, direcciones) enviados a servidores de terceros.",
                    "Privacidad por Diseño. Cero bytes de información sensible abandonan el terminal físico. Cumplimiento estricto del RGPD europeo."
                ),
                listOf(
                    "Fiabilidad Offline",
                    "Requiere conectividad móvil ininterrumpida de baja latencia. Inutilizable en sótanos o aulas sin cobertura.",
                    "100% Offline-First. Completamente operativa en modo avión con consumo nulo de datos móviles."
                ),
                listOf(
                    "Latencia de Red",
                    "400 ms – 1500 ms de penalización por handshakes TLS y transporte de peticiones HTTP.",
                    "0 ms de latencia de red. Fast-path determinista inmediato o inferencia directa sobre hardware local."
                )
            )
        )

        ArticleSectionHeader("2. Elección del Motor de Inferencia: ¿Por Qué Google MediaPipe Tasks GenAI?")

        ArticleParagraph(
            "Ejecutar Modelos de Lenguaje (LLMs) en teléfonos inteligentes con recursos restringidos requiere un runtime de inferencia especializado. Aunque existen alternativas como llama.cpp o ExecuTorch en el ecosistema open source, para HomeTutorPro seleccionamos Google MediaPipe Tasks GenAI por ventajas técnicas determinantes:"
        )

        BulletItem("Aceleración NDK de Primer Nivel en Android:", "Optimización directa de pipelines en GPU, OpenCL, Vulkan y NPU diseñada por Google para chipsets Qualcomm Snapdragon, MediaTek Dimensity y Google Tensor.")
        BulletItem("Integración Nativa en Gradle con Kotlin:", "Elimina la necesidad de compilar y mantener wrappers manuales JNI/C++ inestables dentro del repositorio de la aplicación.")
        BulletItem("Compatibilidad Nativa con Gemma:", "Ejecución fluida de empaquetados cuantizados .task y .bin.")
        BulletItem("Soporte de Páginas de Memoria de 16 KB:", "Cumplimiento total con los requerimientos de páginas de 16 KB de Android 15 y Android 17 a partir de la versión 0.10.27+.")

        ArticleSectionHeader("3. Implementación con Clean Architecture")

        ArticleParagraph(
            "Para evitar el acoplamiento con el proveedor de IA y mantener intacta la pureza de la lógica de negocio, diseñamos una Clean Architecture estricta de 3 capas con Flujo de Datos Unidireccional (UDF):"
        )

        CodeSnippet(
            code = """┌────────────────────────────────────────────────────────┐
│               PRESENTATION LAYER (UI)                  │
│   Jetpack Compose • ViewModels • TTS Engine • StateFlow│
└───────────────────────────┬────────────────────────────┘
                            │ Observa StateFlow / Emite Eventos
                            ▼
┌────────────────────────────────────────────────────────┐
│                 DOMAIN LAYER (Core)                    │
│   Entities • UseCases • SueAgent                       │
│   (100% Kotlin Puro — Cero Dependencias de Android SDK)│
└───────────────────────────┬────────────────────────────┘
                            │ Implementa Interfaces de Repositorio
                            ▼
┌────────────────────────────────────────────────────────┐
│                  DATA LAYER (Infra)                    │
│   Room DB • MediaPipe GenAI C++ • Servicios de Audio   │
└────────────────────────────────────────────────────────┘""",
            language = "Arquitectura"
        )

        ArticleParagraph(
            "El agente de dominio (SueAgent) orquesta la construcción de prompts y la detección de intenciones sin saber si la inferencia la resuelve MediaPipe o un doble de test simulado."
        )

        ArticleSectionHeader("4. RAG Local (Retrieval-Augmented Generation) sobre SQLite")

        ArticleSubHeader("¿Qué es el RAG Local?")
        ArticleParagraph(
            "Los Modelos de Lenguaje Pequeños (SLMs) de 1B–2B parámetros no pueden almacenar bases de datos factuales extensas sin sufrir alucinaciones severas. La técnica RAG soluciona esto en tres pasos:"
        )

        BulletItem("1. Recuperación (Retrieval):", "Consulta la base de datos local Room (SQLite) buscando los registros relevantes (saldos de alumnos, agenda semanal, PDFs didácticos compartidos).")
        BulletItem("2. Aumento (Augmentation):", "Inyecta los resultados estructurados de la base de datos directamente en el bloque --- DATOS DISPONIBLES --- del prompt.")
        BulletItem("3. Generación (Generation):", "Restringe a Gemma para responder utilizando única y exclusivamente el contexto inyectado.")

        CodeSnippet(
            code = """Voz del Usuario -> Android SpeechRecognizer -> Texto de Consulta
       │
       ▼
SueViewModel -> sueAgent.detectActionIntent(query)
       ├── [Fast-Path: Consulta SQL en 0 ms]
       └── [Ruta Generativa: RAG Local + MediaPipe]
              ├── sueAgent.gatherRelevantContext(query)
              │   (Extracción selectiva en Room DB)
              ├── Presupuestado de Tokens y Agrupación
              │   (Pre-compresión relacional en Kotlin)
              ├── Inyección en Gemma Chat Template (2048)
              └── Inferencia en Dispositivo -> Reproducción TTS""",
            language = "Pipeline RAG"
        )

        ArticleSubHeader("Presupuestado de Tokens y Pre-Compresión en GPU/NPU")
        ArticleParagraph(
            "Las GPUs móviles disponen de memoria VRAM estrictamente limitada. Volcar filas sin procesar desde la base de datos satura el subsistema gráfico y provoca cierres por falta de memoria (OOM). Por ello, aplicamos reglas proactivas de compresión en Kotlin antes de construir el prompt:"
        )

        BulletItem("Agregación Relacional:", "Al consultar recursos educativos compartidos: si el total de archivos es ≤ 15, inyectar el desglose cronológico completo; si el total de archivos es > 15, aplicar agrupación semántica por estudiante (groupBy { it.studentId }), inyectando el recuento total y únicamente el recurso más reciente de cada alumno.")
        BulletItem("Ampliación de la Ventana de Contexto (MAX_TOKENS = 2048):", "Configurado en MediaPipeModelRepository (TEMPERATURE = 0.3f, TOP_K = 20) para permitir consultas combinadas (por ejemplo: clases de hoy + huecos libres + cobros pendientes) sin truncar las oraciones de respuesta.")

        ArticleSectionHeader("5. Gemma Chat Template e Ingeniería de Prompts")

        ArticleParagraph("El prompt ensamblado sigue estrictamente el formato oficial de turnos de Google Gemma:")

        CodeSnippet(
            code = """<start_of_turn>user
Eres Sue, la asistente inteligente integrada en el dispositivo para HomeTutorPro.
Tu función es responder a las preguntas del profesor de forma concisa y rigurosa.
NUNCA inventes datos. Utiliza ÚNICAMENTE la información provista en --- DATOS DISPONIBLES ---.

--- CONTEXTO TEMPORAL ---
Fecha y hora actual: Martes, 05 Oct 2026, 09:30
--- FIN DEL CONTEXTO TEMPORAL ---

--- HORARIO DE JORNADA (LÍMITES DE AGENDA) ---
Horario de disponibilidad: 08:00 a 22:00
--- FIN DE HORARIO DE JORNADA ---

--- DATOS DISPONIBLES ---
Agenda del Martes:
  16:30–18:00: Lucía Moreno (Álgebra)
  18:30–19:30: Carlos (Física)
Huecos libres dentro de la jornada:
  08:00–16:30, 19:30–22:00
--- FIN DE DATOS ---

Pregunta del profesor:
¿Qué clases tengo esta tarde y en qué huecos estoy libre?
<end_of_turn>
<start_of_turn>model""",
            language = "Template de Prompt"
        )

        ArticleSectionHeader("6. Conclusiones Clave para la Ingeniería Móvil")

        BulletItem("La Cuantización es Imprescindible:", "Gemma 3 INT4 reduce el tamaño del modelo de 4 GB a ~550 MB en disco y ~800 MB en RAM activa, permitiendo su ejecución estable en teléfonos Android estándar de gama media (2,5+ GB de RAM).")
        BulletItem("Pre-Filtrado en Kotlin:", "No delegues el filtrado pesado de información en el modelo de lenguaje. Utiliza índices SQL en Room y funciones relacionales en Kotlin antes de enviar el contexto a la IA.")
        BulletItem("Desacopla la Inferencia:", "Aísla el motor de Machine Learning tras una interfaz limpia en la capa de dominio para garantizar testabilidad unitaria rápida y determinista.")

        CalloutBox {
            ArticleParagraph(
                "💡 Regla de Oro Arquitectónica: Trata el LLM local como un sintetizador elocuente, nunca como un motor de lógica o base de datos sin límites. Al combinar consultas SQLite deterministas con presupuestado de tokens estricto, la IA on-device ofrece latencia cero, privacidad absoluta y 0€ de coste operativo."
            )
        }
    }
}
